// Cognito hosted-UI Authorization Code + PKCE orchestration.
import { config } from '../config';
import { generateCodeVerifier, codeChallengeFromVerifier, generateState } from './pkce';
import {
  saveTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  hasSession,
  isExpired,
} from './tokens';

const VERIFIER_KEY = 'ip.pkceVerifier';
const STATE_KEY = 'ip.oauthState';
const RETURN_KEY = 'ip.returnTo';

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

// Kick off sign-in: create + store the PKCE verifier and state, then send the
// browser to the Cognito hosted UI authorize endpoint.
export async function beginLogin(returnTo = '/jobs'): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeFromVerifier(verifier);
  const state = generateState();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

// Handle the /auth/callback redirect: validate state, exchange the code for
// tokens using the stored verifier. Returns the path to navigate to next.
export async function completeLogin(search: string): Promise<string> {
  const query = new URLSearchParams(search);
  const error = query.get('error');
  if (error) {
    throw new Error(query.get('error_description') || error);
  }
  const code = query.get('code');
  const state = query.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) || '/jobs';

  if (!code) throw new Error('Sign-in did not return an authorization code.');
  if (!state || state !== expectedState) throw new Error('Sign-in state mismatch. Please try again.');
  if (!verifier) throw new Error('Missing PKCE verifier. Please start sign-in again.');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Token exchange failed (${res.status}). ${detail}`.trim());
  }
  const tokens = (await res.json()) as {
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
  };
  saveTokens(tokens);

  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  // Fire-and-forget: stamp the user's MFA preference so admin-get-user reflects the TOTP
  // factor the hosted UI enrolled. Pool-level MFA "ON" enforces the challenge either way;
  // this only keeps the per-user record audit-accurate (see runbook 3.1).
  void setMfaPreference(tokens.access_token);

  return returnTo;
}

// Cognito hosted-UI enrolment verifies the software token but never writes the per-user
// MFA preference, so UserMFASettingList stays empty. Set it explicitly after sign-in.
// Best-effort: failures are ignored, sign-in must never break over a bookkeeping call.
async function setMfaPreference(accessToken: string): Promise<void> {
  try {
    await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.SetUserMFAPreference',
      },
      body: JSON.stringify({
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
      }),
    });
  } catch {
    // best-effort only
  }
}

let refreshInFlight: Promise<boolean> | null = null;

// Exchange the refresh token for a fresh access token. Returns false on failure.
export async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.userPoolClientId,
        refresh_token: refreshToken,
      });
      const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) return false;
      const tokens = (await res.json()) as {
        access_token: string;
        id_token?: string;
        refresh_token?: string;
        expires_in: number;
      };
      saveTokens(tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// Return a valid access token, refreshing proactively if it is near expiry.
export async function ensureAccessToken(): Promise<string | null> {
  if (!getAccessToken()) return null;
  if (isExpired()) {
    const ok = await refreshTokens();
    if (!ok) {
      // The refresh token is dead (expired or revoked). Purge the stale tokens
      // so hasSession()/isAuthenticated() stop reporting a session that no longer
      // works. Without this the SignIn page keeps bouncing back to the guarded
      // route (both still see the leftover tokens as "authenticated"), producing
      // an endless redirect loop on an apparently frozen blank page.
      clearTokens();
      return null;
    }
  }
  return getAccessToken();
}

export function isAuthenticated(): boolean {
  return hasSession();
}

export function logout(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
}
